import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import optimizationsApi from '../../api/optimizations';
import {
  clearAllJobSlots,
  clearJobSlot,
  readJobMap,
  removeLegacyJobKey,
  setJobSlot,
} from '../../utils/optimizationJobStorage';
import {
  getOptimizationJobUILabel,
  getRowVisualState,
} from '../../utils/optimizationJobPhaseLabels';
import { normalizeGroupsResponse } from '../../utils/optimizationTransformers';
import {
  mergePendingBatch,
  normalizePlatformsArg,
} from '../../utils/optimizationGenerationQueue';
import { AuthContext } from './AuthContext';

const OptimizationGenerationToastContext = createContext(null);

const POLL_MS = 2000;
const AUTO_DISMISS_MS = 2000;

const INITIAL_STATE = {
  isVisible: false,
  status: 'idle',
  progress: 0,
  foundCount: 0,
  brandId: null,
  message: '',
  error: '',
  jobServerStatus: null,
  activePlatforms: [],
  rows: { google: null, meta: null },
};

const defaultResumeLoadCount = async () => {
  const response = await optimizationsApi.getActiveOptimizations();
  const { groups } = normalizeGroupsResponse(response?.data);
  return groups.length;
};

function buildRowState(platform, payload, ttlExpired) {
  const serverError = payload?.status === 'failed' ? payload?.error || '' : '';
  return {
    label: getOptimizationJobUILabel(platform, payload),
    visual: getRowVisualState(platform, payload, ttlExpired),
    serverError,
  };
}

async function migrateLegacyJob(brandId) {
  const map = readJobMap(brandId);
  if (!map.legacyJobId) return;
  const lid = map.legacyJobId;
  try {
    const d = await optimizationsApi.getJobStatus(lid);
    const pl = d?.platform === 'meta' ? 'meta' : 'google';
    setJobSlot(brandId, pl, lid);
  } catch (e) {
    if (e?.response?.status !== 404) {
      console.warn('migrateLegacyJob getJobStatus:', e);
    }
  } finally {
    removeLegacyJobKey(brandId);
  }
}

export const OptimizationGenerationToastProvider = ({ children }) => {
  const { auth } = useContext(AuthContext);
  const [state, setState] = useState(INITIAL_STATE);
  const [isGenerating, setIsGenerating] = useState(false);

  const pollIntervalRef = useRef(null);
  const pollInFlightRef = useRef(false);
  const loadCountRef = useRef(null);
  const lastParamsRef = useRef(null);
  const resumeHandledRef = useRef(null);
  const outcomeRef = useRef(null);
  const autoDismissRef = useRef(null);

  const brandIdRef = useRef(null);
  const activePlatformsRef = useRef([]);
  const jobIdByPlatformRef = useRef({});
  const lastSuggestionsCountRef = useRef({ google: null, meta: null });
  const terminalRef = useRef({ google: null, meta: null });
  /** Last API payload when a platform reached a terminal state (for stable row labels). */
  const terminalPayloadRef = useRef({ google: null, meta: null });

  /** FIFO of pending optimization runs; same-brand requests are merged (union platforms). */
  const pendingQueueRef = useRef([]);
  /** Mirrors generating state for queue decisions without stale closures. */
  const isGeneratingRef = useRef(false);
  const processPendingQueueRef = useRef(() => {});

  const clearPollInterval = useCallback(() => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
  }, []);

  const clearAutoDismiss = useCallback(() => {
    if (autoDismissRef.current) {
      clearTimeout(autoDismissRef.current);
      autoDismissRef.current = null;
    }
  }, []);

  const clearAllTimers = useCallback(() => {
    clearPollInterval();
    clearAutoDismiss();
  }, [clearAutoDismiss, clearPollInterval]);

  const dispatchIncrementalRefresh = useCallback((brandId) => {
    if (typeof window === 'undefined' || !brandId) return;
    window.dispatchEvent(
      new CustomEvent('optimization-suggestions-incremental-refresh', {
        detail: { brandId },
      })
    );
  }, []);

  const finalizeAllJobs = useCallback(
    async (brandId, platforms, loadCountFn, rowsSnapshot, termSnapshot) => {
      clearPollInterval();
      clearAutoDismiss();

      let count = 0;
      try {
        count = await loadCountFn();
      } catch (e) {
        console.error('loadCount after jobs finished:', e);
      }

      if (typeof window !== 'undefined') {
        window.dispatchEvent(
          new CustomEvent('optimization-suggestions-job-finished', {
            detail: { brandId, count },
          })
        );
      }

      lastParamsRef.current = {
        brandId,
        loadCount: loadCountFn,
        platforms: [...platforms],
      };
      resumeHandledRef.current = null;

      if (outcomeRef.current) {
        outcomeRef.current({ ok: true, count });
        outcomeRef.current = null;
      }

      if (pendingQueueRef.current.length > 0) {
        processPendingQueueRef.current();
      } else {
        setIsGenerating(false);
        isGeneratingRef.current = false;
      }

      const allFailed = platforms.every((p) => termSnapshot[p] === 'failed');
      const anyDoneOk = platforms.some(
        (p) => termSnapshot[p] === 'completed' || termSnapshot[p] === 'ttl404'
      );

      if (allFailed) {
        const firstErr = platforms
          .map((p) => rowsSnapshot[p]?.serverError)
          .find(Boolean);
        setState((prev) => ({
          ...prev,
          isVisible: true,
          status: 'error',
          progress: 100,
          error: firstErr || 'Error al generar optimizaciones',
          message: 'No pudimos generar optimizaciones.',
          brandId,
          activePlatforms: platforms,
          rows: { ...rowsSnapshot },
        }));
        return;
      }

      if (count > 0 || anyDoneOk) {
        const rows = { ...rowsSnapshot };
        platforms.forEach((p) => {
          if (rows[p] && rows[p].visual !== 'error') {
            rows[p] = { ...rows[p], visual: 'done' };
          }
        });
        setState((prev) => ({
          ...prev,
          isVisible: true,
          status: 'success',
          progress: 100,
          foundCount: count,
          brandId,
          message:
            count > 0
              ? `Listo: ${count} grupo${count === 1 ? '' : 's'} de optimizaciones activas.`
              : 'Generación finalizada.',
          error: '',
          activePlatforms: platforms,
          rows,
        }));
        autoDismissRef.current = setTimeout(() => {
          setState(INITIAL_STATE);
          autoDismissRef.current = null;
        }, AUTO_DISMISS_MS);
      } else {
        setState({
          isVisible: true,
          status: 'empty',
          progress: 100,
          foundCount: 0,
          brandId,
          message: 'No encontramos optimizaciones por ahora. Intentá más tarde.',
          error: '',
          jobServerStatus: null,
          activePlatforms: platforms,
          rows: { ...rowsSnapshot },
        });
        autoDismissRef.current = setTimeout(() => {
          setState(INITIAL_STATE);
          autoDismissRef.current = null;
        }, AUTO_DISMISS_MS);
      }
    },
    [clearAutoDismiss, clearPollInterval]
  );

  const runPollTick = useCallback(() => {
    const brandId = brandIdRef.current;
    const platforms = activePlatformsRef.current;
    const loadCountFn = loadCountRef.current;
    if (!brandId || !platforms.length || !loadCountFn) return;

    const tick = async () => {
      if (pollInFlightRef.current) return;
      pollInFlightRef.current = true;
      try {
        const jobIds = jobIdByPlatformRef.current;
        const results = await Promise.all(
          platforms.map(async (p) => {
            const id = jobIds[p];
            if (!id) return { platform: p, error: null, data: null, notFound: false };
            try {
              const data = await optimizationsApi.getJobStatus(id);
              return { platform: p, error: null, data, notFound: false };
            } catch (err) {
              if (err?.response?.status === 404) {
                return { platform: p, error: err, data: null, notFound: true };
              }
              return { platform: p, error: err, data: null, notFound: false };
            }
          })
        );

        const rows = { google: null, meta: null };
        let aggregateProgress = 20;

        for (const { platform: p, data, notFound } of results) {
          if (terminalRef.current[p]) {
            const payload = terminalPayloadRef.current[p];
            rows[p] = buildRowState(
              p,
              payload || { status: 'completed', result: { total_suggestions_created: 0 } },
              terminalRef.current[p] === 'ttl404'
            );
            aggregateProgress += 40;
            continue;
          }

          if (notFound) {
            clearJobSlot(brandId, p);
            delete jobIdByPlatformRef.current[p];
            terminalRef.current[p] = 'ttl404';
            terminalPayloadRef.current[p] = {
              status: 'completed',
              result: { total_suggestions_created: 0 },
            };
            rows[p] = buildRowState(p, terminalPayloadRef.current[p], true);
            dispatchIncrementalRefresh(brandId);
            aggregateProgress += 40;
            continue;
          }

          if (!data?.status) {
            rows[p] = buildRowState(p, { status: 'pending' }, false);
            aggregateProgress += 15;
            continue;
          }

          if (data.status === 'pending' || data.status === 'running') {
            const sc = data.suggestions_count;
            const last = lastSuggestionsCountRef.current[p];
            if (sc != null && last !== sc) {
              lastSuggestionsCountRef.current[p] = sc;
              dispatchIncrementalRefresh(brandId);
            }
            rows[p] = buildRowState(p, data, false);
            aggregateProgress += data.status === 'running' ? 35 : 25;
            continue;
          }

          if (data.status === 'completed') {
            clearJobSlot(brandId, p);
            delete jobIdByPlatformRef.current[p];
            terminalRef.current[p] = 'completed';
            terminalPayloadRef.current[p] = data;
            lastSuggestionsCountRef.current[p] = data.suggestions_count ?? null;
            dispatchIncrementalRefresh(brandId);
            rows[p] = buildRowState(p, data, false);
            aggregateProgress += 40;
            continue;
          }

          if (data.status === 'failed') {
            clearJobSlot(brandId, p);
            delete jobIdByPlatformRef.current[p];
            terminalRef.current[p] = 'failed';
            terminalPayloadRef.current[p] = data;
            rows[p] = buildRowState(p, data, false);
            aggregateProgress += 40;
          }
        }

        const allTerminal = platforms.every((p) => Boolean(terminalRef.current[p]));
        const nextRows = { google: null, meta: null };
        platforms.forEach((p) => {
          nextRows[p] = rows[p];
        });
        setState((prev) => ({
          ...prev,
          status: 'polling',
          progress: Math.min(95, aggregateProgress),
          rows: nextRows,
          activePlatforms: platforms,
          brandId,
        }));

        if (allTerminal) {
          clearPollInterval();
          await finalizeAllJobs(brandId, platforms, loadCountFn, rows, {
            ...terminalRef.current,
          });
        }
      } finally {
        pollInFlightRef.current = false;
      }
    };

    tick();
    pollIntervalRef.current = setInterval(tick, POLL_MS);
  }, [dispatchIncrementalRefresh, finalizeAllJobs, clearPollInterval]);

  const startDualGeneration = useCallback(
    async (brandId, loadCountFn, platforms, options = {}) => {
      const { resumeJobIds = null, skipOutcome = false } = options;

      await migrateLegacyJob(brandId);

      loadCountRef.current = loadCountFn;
      brandIdRef.current = brandId;
      activePlatformsRef.current = [...platforms];
      jobIdByPlatformRef.current = resumeJobIds ? { ...resumeJobIds } : {};
      lastSuggestionsCountRef.current = { google: null, meta: null };
      terminalRef.current = { google: null, meta: null };
      terminalPayloadRef.current = { google: null, meta: null };

      setIsGenerating(true);
      isGeneratingRef.current = true;
      clearAllTimers();

      const initialRows = { google: null, meta: null };
      platforms.forEach((p) => {
        initialRows[p] = buildRowState(p, { status: 'pending' }, false);
      });

      setState({
        isVisible: true,
        status: 'polling',
        progress: 15,
        foundCount: 0,
        brandId,
        message: resumeJobIds ? 'Reanudando generación...' : 'Iniciando generación de optimizaciones...',
        error: '',
        jobServerStatus: null,
        activePlatforms: platforms,
        rows: initialRows,
      });

      try {
        if (!resumeJobIds) {
          await Promise.all(
            platforms.map(async (p) => {
              const map = readJobMap(brandId);
              let jobId = map[p];
              if (jobId) {
                try {
                  const st = await optimizationsApi.getJobStatus(jobId);
                  if (st?.status === 'pending' || st?.status === 'running') {
                    jobIdByPlatformRef.current[p] = jobId;
                    return;
                  }
                  clearJobSlot(brandId, p);
                } catch (e) {
                  if (e?.response?.status === 404) {
                    clearJobSlot(brandId, p);
                  } else {
                    throw e;
                  }
                }
              }
              const result = await optimizationsApi.generateSuggestions(brandId, p);
              const newId = result.jobId;
              if (!newId) {
                throw new Error('No se recibió job_id');
              }
              setJobSlot(brandId, p, newId);
              jobIdByPlatformRef.current[p] = newId;
            })
          );
        } else {
          Object.entries(resumeJobIds).forEach(([p, id]) => {
            if (platforms.includes(p) && id) {
              jobIdByPlatformRef.current[p] = id;
            }
          });
        }

        runPollTick();
      } catch (error) {
        clearAllTimers();
        clearAllJobSlots(brandId);
        resumeHandledRef.current = null;
        terminalRef.current = { google: null, meta: null };
        terminalPayloadRef.current = { google: null, meta: null };
        if (!skipOutcome && outcomeRef.current) {
          outcomeRef.current({ ok: false, count: 0, error });
          outcomeRef.current = null;
        }
        if (pendingQueueRef.current.length > 0) {
          processPendingQueueRef.current();
        } else {
          setIsGenerating(false);
          isGeneratingRef.current = false;
        }
        const msg =
          error?.response?.data?.error ||
          error?.response?.data?.message ||
          error?.message ||
          'Error al generar optimizaciones';
        setState((prev) => ({
          ...prev,
          isVisible: true,
          status: 'error',
          progress: 100,
          error: msg,
          message: 'No pudimos generar optimizaciones.',
          activePlatforms: platforms,
          rows: prev.rows,
        }));
        return { ok: false, count: 0, error };
      }

      return { ok: true, pending: true };
    },
    [clearAllTimers, runPollTick]
  );

  const processPendingQueue = useCallback(() => {
    const batch = pendingQueueRef.current.shift();
    if (!batch) {
      setIsGenerating(false);
      isGeneratingRef.current = false;
      return;
    }
    lastParamsRef.current = {
      brandId: batch.brandId,
      loadCount: batch.loadCount,
      platforms: batch.platforms,
    };
    outcomeRef.current = (result) => {
      batch.resolvers.forEach((fn) => fn(result));
    };
    void startDualGeneration(batch.brandId, batch.loadCount, batch.platforms, {});
  }, [startDualGeneration]);

  useEffect(() => {
    processPendingQueueRef.current = processPendingQueue;
  }, [processPendingQueue]);

  const requestGeneration = useCallback(
    async ({ brandId, loadCount, platforms: platformsArg }) => {
      if (!brandId || typeof loadCount !== 'function') {
        return { ok: false, count: 0 };
      }
      const platforms = normalizePlatformsArg(platformsArg, auth?.user);
      if (platforms.length === 0) {
        return { ok: false, count: 0 };
      }

      return new Promise((resolve) => {
        if (!isGeneratingRef.current) {
          lastParamsRef.current = { brandId, loadCount, platforms };
          clearAllTimers();
          outcomeRef.current = resolve;
          startDualGeneration(brandId, loadCount, platforms, {}).then((started) => {
            if (!started?.ok) {
              outcomeRef.current = null;
              resolve(started);
            }
          });
        } else {
          mergePendingBatch(pendingQueueRef.current, brandId, loadCount, platforms, resolve);
        }
      });
    },
    [auth?.user, clearAllTimers, startDualGeneration]
  );

  const runGeneration = requestGeneration;

  const retryGeneration = useCallback(async () => {
    const params = lastParamsRef.current;
    if (!params?.brandId || typeof params.loadCount !== 'function') {
      return { ok: false, count: 0 };
    }
    return runGeneration({
      brandId: params.brandId,
      loadCount: params.loadCount,
      platforms: params.platforms,
    });
  }, [runGeneration]);

  const dismissToast = useCallback(() => {
    clearAutoDismiss();
    setState((prev) => {
      const terminal = ['success', 'empty', 'error', 'idle'].includes(prev.status);
      const idleLike = prev.status === 'idle';
      if (terminal && !idleLike) {
        return INITIAL_STATE;
      }
      if (idleLike) {
        return INITIAL_STATE;
      }
      return { ...prev, isVisible: false };
    });
  }, [clearAutoDismiss]);

  const closeSuccessToastOnRoute = useCallback(() => {
    clearAutoDismiss();
    setState((prev) => {
      if (
        (prev.status === 'success' || prev.status === 'empty') &&
        prev.isVisible
      ) {
        return INITIAL_STATE;
      }
      return prev;
    });
  }, [clearAutoDismiss]);

  useEffect(() => {
    const brandId = auth?.user?.active_brand_id;
    if (!brandId || isGenerating) return;

    let cancelled = false;

    (async () => {
      await migrateLegacyJob(brandId);
      if (cancelled) return;

      const map = readJobMap(brandId);
      const resumePlatforms = [];
      const resumeIds = {};

      for (const p of ['google', 'meta']) {
        const id = map[p];
        if (!id) continue;
        try {
          const d = await optimizationsApi.getJobStatus(id);
          if (d?.status === 'pending' || d?.status === 'running') {
            resumePlatforms.push(p);
            resumeIds[p] = id;
          } else {
            clearJobSlot(brandId, p);
          }
        } catch (e) {
          if (e?.response?.status === 404) {
            clearJobSlot(brandId, p);
          }
        }
      }

      if (cancelled || resumePlatforms.length === 0) {
        resumeHandledRef.current = null;
        return;
      }

      const key = `${brandId}:${resumePlatforms.sort().join(',')}`;
      if (resumeHandledRef.current === key) return;
      resumeHandledRef.current = key;

      loadCountRef.current = defaultResumeLoadCount;
      lastParamsRef.current = {
        brandId,
        loadCount: defaultResumeLoadCount,
        platforms: resumePlatforms,
      };

      await startDualGeneration(brandId, defaultResumeLoadCount, resumePlatforms, {
        resumeJobIds: resumeIds,
        skipOutcome: true,
      });
    })();

    return () => {
      cancelled = true;
    };
  }, [auth?.user?.active_brand_id, isGenerating, startDualGeneration]);

  useEffect(
    () => () => {
      clearAllTimers();
    },
    [clearAllTimers]
  );

  const value = useMemo(
    () => ({
      state,
      isGenerating,
      runGeneration,
      requestGeneration,
      retryGeneration,
      dismissToast,
      closeSuccessToastOnRoute,
    }),
    [
      closeSuccessToastOnRoute,
      dismissToast,
      isGenerating,
      retryGeneration,
      requestGeneration,
      runGeneration,
      state,
    ]
  );

  return (
    <OptimizationGenerationToastContext.Provider value={value}>
      {children}
    </OptimizationGenerationToastContext.Provider>
  );
};

OptimizationGenerationToastProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export const useOptimizationGenerationToast = () => {
  const context = useContext(OptimizationGenerationToastContext);
  if (!context) {
    throw new Error(
      'useOptimizationGenerationToast debe usarse dentro de OptimizationGenerationToastProvider'
    );
  }
  return context;
};
